import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ChevronRight, Menu, Monitor, Sun, Moon } from "lucide-react";

import { CURRENT_USER_CACHE_EVENT, getAccountSets, getCurrentUser } from "./services/sessionCache";
import { resolveAccountSetId, setStoredAccountSetId } from "./services/accountSetSelection";
import { connectWS, subscribeToLockedAccountSets, onMessage } from "./services/wsService";
import { ensureUnlocked, playOrderClosed } from "./services/sound";

import NavBar from "./components/NavBar";
import BroadcastToast from "./components/BroadcastToast";
import AppPageLoader from "./components/AppPageLoader";
import { ToastProvider } from "./components/Toast";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Network from "./pages/Network";
import ForgotPassword from "./pages/ForgotPassword";
import LinkAccount from "./pages/LinkAccount";
import Settings from "./pages/Settings";
import TelegramReporting from "./pages/TelegramReporting";
import TradeExecution from "./components/TradeExecution";
import ActiveTrades from "./pages/ActiveTrades";
import ClosedTrades from "./pages/ClosedTrades";
import PendingTrades from "./pages/PendingTrades";
import AdminOverviewPage from "./pages/admin/AdminOverviewPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminAccountsPage from "./pages/admin/AdminAccountsPage";
import AdminBroadcastPage from "./pages/admin/AdminBroadcastPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

function TradeExecutionWrapper() {
  const [accountSets, setAccountSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAccountSets()
      .then((accountSetsData) => {
        if (Array.isArray(accountSetsData)) {
          setAccountSets(accountSetsData);
          if (accountSetsData.length > 0) {
            const selectedId = resolveAccountSetId(accountSetsData);
            setSelectedSetId(selectedId);
            if (selectedId) setStoredAccountSetId(selectedId);
          }
        } else {
          setAccountSets([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("TradeExecutionWrapper: Failed to load account-sets:", err);
        setAccountSets([]);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <AppPageLoader
        label="Loading Trade Execution"
        detail="Fetching account sets and preparing execution controls..."
      />
    );
  }

  const selectedAccountSet = accountSets.find(
    (set) => String(set._id || set.id || "") === String(selectedSetId || ""),
  );

  return (
    <TradeExecution
      accountSet={selectedAccountSet}
      accountSets={accountSets}
      selectedSetId={selectedSetId}
      onAccountSetChange={(nextId) => {
        setSelectedSetId(nextId);
        if (nextId) setStoredAccountSetId(nextId);
      }}
    />
  );
}

function PrivateRoute({ children }) {
  return localStorage.getItem("token") ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  return localStorage.getItem("token") ? <Navigate to="/dashboard" replace /> : children;
}

function AdminRoute({ children }) {
  const [allowed, setAllowed] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        if (user?.role === "admin") setAllowed(true);
        else nav("/dashboard");
      })
      .catch(() => nav("/login"));
  }, [nav]);

  if (allowed === null) {
    return (
      <AppPageLoader
        label="Checking admin access"
        detail="Verifying permissions and preparing the admin workspace..."
      />
    );
  }
  return children;
}

function RealtimeBootstrap() {
  useEffect(() => {
    if (!localStorage.getItem("token")) return;

    getAccountSets()
      .then((accountSetsData) => {
        if (!Array.isArray(accountSetsData) || accountSetsData.length === 0) return;
        const selectedId = resolveAccountSetId(accountSetsData);
        subscribeToLockedAccountSets(accountSetsData, { connectAccountSetId: selectedId });
        if (selectedId) connectWS(selectedId);
      })
      .catch((err) => {
        console.error("RealtimeBootstrap: Failed to initialize realtime subscriptions:", err);
      });
  }, []);

  return null;
}

function GlobalTradeCloseAudio() {
  const playedCloseKeysRef = useRef(new Set());

  useEffect(() => {
    ensureUnlocked();

    const handleTradeClose = (payload) => {
      const data = payload?.data ?? payload ?? {};
      const tradeId = String(data?.tradeId ?? payload?.tradeId ?? "").trim();
      const accountSetId = String(data?.accountSetId ?? payload?.accountSetId ?? "").trim();
      const broker1Ticket = String(data?.broker1Ticket ?? payload?.broker1Ticket ?? "").trim();
      const broker2Ticket = String(data?.broker2Ticket ?? payload?.broker2Ticket ?? "").trim();
      const ticketKeyParts = [broker1Ticket, broker2Ticket].filter(Boolean);
      const soundKey =
        tradeId ||
        (ticketKeyParts.length ? [accountSetId, ...ticketKeyParts].filter(Boolean).join(":") : "");

      if (soundKey && playedCloseKeysRef.current.has(soundKey)) return;

      try {
        playOrderClosed();
      } catch {}

      if (soundKey) playedCloseKeysRef.current.add(soundKey);
    };

    const offClosedTradeCreated = onMessage("closed_trade_created", handleTradeClose);
    const offTradeClosed = onMessage("trade_closed", handleTradeClose);

    return () => {
      offClosedTradeCreated?.();
      offTradeClosed?.();
    };
  }, []);

  return null;
}

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "system");

  useEffect(() => {
    const applyTheme = (pref) => {
      const resolved =
        pref === "system"
          ? window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light"
          : pref;
      document.documentElement.setAttribute("data-theme", resolved);
    };
    applyTheme(theme);
    localStorage.setItem("theme", theme);

    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme("system");
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, [theme]);

  const cycleTheme = () =>
    setTheme((prev) => (prev === "system" ? "light" : prev === "light" ? "dark" : "system"));
  return { theme, cycleTheme };
}

function getAvatarInitials(name, email) {
  const base = String(name || email || "User").trim();
  if (!base) return "U";

  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase() || "U";
}

function TopBar({ isMobile, onOpenSidebar, collapsed, theme, cycleTheme }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    let active = true;

    const syncCurrentUser = () => {
      getCurrentUser()
        .then((user) => {
          if (active) setCurrentUser(user || null);
        })
        .catch(() => {
          if (active) setCurrentUser(null);
        });
    };

    const handleCurrentUserUpdate = (event) => {
      if (!active) return;
      setCurrentUser(event.detail || null);
    };

    syncCurrentUser();
    window.addEventListener(CURRENT_USER_CACHE_EVENT, handleCurrentUserUpdate);

    return () => {
      active = false;
      window.removeEventListener(CURRENT_USER_CACHE_EVENT, handleCurrentUserUpdate);
    };
  }, []);

  const routeMap = {
    dashboard: "Dashboard",
    "active-trades": "Active Trades",
    "closed-trades": "Closed Trades",
    "pending-orders": "Pending Orders",
    "trade-execution": "Trade Execution",
    network: "My Network",
    "link-account": "Link Account",
    "telegram-hub": "Telegram Hub",
    settings: "Settings",
    admin: "Admin",
    overview: "Overview",
    users: "Users",
    accounts: "Accounts",
    broadcast: "Broadcast Hub",
  };

  const segments = location.pathname.split("/").filter(Boolean);
  const isDashboardRoute = location.pathname === "/dashboard";
  const crumbs = segments.map((segment, index) => {
    const path = `/${segments.slice(0, index + 1).join("/")}`;
    return {
      path,
      label: routeMap[segment] || segment.replace(/-/g, " "),
    };
  });
  const breadcrumbItems = isDashboardRoute
    ? [
        { path: "/dashboard", label: "Premix AI", kind: "brand" },
        { path: "/dashboard", label: "Home", kind: "current" },
      ]
    : [
        { path: "/dashboard", label: "Home", kind: "link" },
        ...crumbs
          .filter((crumb) => crumb.path !== "/dashboard")
          .map((crumb) => ({ ...crumb, kind: "current" })),
      ];

  const avatarUrl = typeof currentUser?.avatarUrl === "string" ? currentUser.avatarUrl.trim() : "";
  const avatarInitials = getAvatarInitials(currentUser?.displayName, currentUser?.email);
  const openSettings = () => navigate("/settings");

  return (
    <header
      className={`app-topbar${collapsed ? " app-topbar-collapsed" : ""}${isMobile ? " app-topbar-mobile" : ""}`}
    >
      <div className="app-topbar-inner">
        <div className="app-topbar-start">
          {isMobile && (
            <button
              className="mobile-sidebar-trigger"
              onClick={onOpenSidebar}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          )}

          <div className="app-topbar-copy">
            <nav className="app-breadcrumbs" aria-label="Breadcrumb">
              {breadcrumbItems.map((crumb, index) => {
                const isLast = index === breadcrumbItems.length - 1;
                return (
                  <React.Fragment key={`${crumb.path}-${crumb.kind}-${index}`}>
                    {index > 0 && (
                      <span className="app-breadcrumb-separator" aria-hidden="true">
                        <ChevronRight size={15} />
                      </span>
                    )}
                    {crumb.kind === "brand" ? (
                      <span className="app-breadcrumb-brand">{crumb.label}</span>
                    ) : isLast ? (
                      <span className="app-breadcrumb-current">{crumb.label}</span>
                    ) : (
                      <Link to={crumb.path} className="app-breadcrumb-link">
                        {crumb.label}
                      </Link>
                    )}
                  </React.Fragment>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="app-topbar-end">
          <button
            className="app-theme-toggle"
            onClick={cycleTheme}
            aria-label={`Theme: ${theme === "system" ? "System" : theme === "light" ? "Light" : "Dark"}. Toggle theme`}
            title={`Theme: ${theme === "system" ? "System" : theme === "light" ? "Light" : "Dark"}`}
          >
            {theme === "system" ? (
              <Monitor size={18} />
            ) : theme === "light" ? (
              <Sun size={18} />
            ) : (
              <Moon size={18} />
            )}
          </button>
          <button
            className="app-profile-button"
            onClick={openSettings}
            aria-label="Open settings"
            title="Settings"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" className="app-profile-image" />
            ) : (
              <span className="app-profile-fallback">{avatarInitials}</span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

function AppRoutes() {
  const location = useLocation();
  const { theme, cycleTheme } = useTheme();
  const publicPaths = [
    "/login",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/privacy-policy",
    "/terms-of-service",
  ];
  const isAuthenticated = Boolean(localStorage.getItem("token"));
  const hideNav = publicPaths.includes(location.pathname) || !isAuthenticated;
  const defaultRedirectPath = isAuthenticated ? "/dashboard" : "/login";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true",
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileSidebarOpen(false);
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  return (
    <>
      {!hideNav && (
        <NavBar
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() =>
            setSidebarCollapsed((prev) => {
              const next = !prev;
              localStorage.setItem("sidebarCollapsed", next);
              return next;
            })
          }
          isMobile={isMobile}
        />
      )}
      {!hideNav && <BroadcastToast />}
      {isAuthenticated && !hideNav && <RealtimeBootstrap />}
      {isAuthenticated && <GlobalTradeCloseAudio />}

      <div
        className={
          hideNav
            ? "app-shell app-shell-public"
            : `app-shell app-shell-private${sidebarCollapsed ? " app-shell-private-collapsed" : ""}${isMobile ? " app-shell-private-mobile" : ""}`
        }
      >
        {!hideNav && (
          <TopBar
            isMobile={isMobile}
            onOpenSidebar={() => setMobileSidebarOpen(true)}
            collapsed={sidebarCollapsed}
            theme={theme}
            cycleTheme={cycleTheme}
          />
        )}
        <Routes>
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <Signup />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/active-trades"
            element={
              <PrivateRoute>
                <ActiveTrades />
              </PrivateRoute>
            }
          />
          <Route
            path="/closed-trades"
            element={
              <PrivateRoute>
                <ClosedTrades />
              </PrivateRoute>
            }
          />
          <Route
            path="/pending-orders"
            element={
              <PrivateRoute>
                <PendingTrades />
              </PrivateRoute>
            }
          />
          <Route
            path="/trade-execution"
            element={
              <PrivateRoute>
                <TradeExecutionWrapper />
              </PrivateRoute>
            }
          />
          <Route
            path="/network"
            element={
              <PrivateRoute>
                <Network />
              </PrivateRoute>
            }
          />
          <Route
            path="/link-account"
            element={
              <PrivateRoute>
                <LinkAccount />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/telegram-hub"
            element={
              <PrivateRoute>
                <TelegramReporting />
              </PrivateRoute>
            }
          />
          <Route path="/telegram-reporting" element={<Navigate to="/telegram-hub" replace />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Navigate to="/admin/overview" replace />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/overview"
            element={
              <AdminRoute>
                <AdminOverviewPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUsersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/accounts"
            element={
              <AdminRoute>
                <AdminAccountsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/broadcast"
            element={
              <AdminRoute>
                <AdminBroadcastPage />
              </AdminRoute>
            }
          />

          <Route path="/" element={<Navigate to={defaultRedirectPath} replace />} />
          <Route path="*" element={<Navigate to={defaultRedirectPath} replace />} />
        </Routes>
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </BrowserRouter>
  );
}
